import React, { useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useServices, Client } from "../../../lib/api";

/**
 * ClientSelector component
 * 
 * Dropdown for selecting a client from the selected instance
 */
interface ClientSelectorProps {
  isTarget?: boolean;
  label?: string;
  serviceId?: number | null;
  selectedClient?: any;
  onClientSelect?: (client: any) => void;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({ 
  isTarget = false,
  label = "Client",
  serviceId,
  selectedClient,
  onClientSelect
}) => {
  const { 
    updateFormField, 
    selectedServiceId, 
    setSelectedServiceId,
    setSelectedClient,
    targetServiceId,
    setTargetServiceId,
    setTargetClient
  } = useWorkflowStore();
  
  // Get the selected instance ID from the form data
  const formData = useWorkflowStore(state => state.formData);
  const instanceId = isTarget ? formData.targetHost : formData.host;
  
  // Use provided serviceId or get from store based on isTarget
  const currentServiceId = serviceId !== undefined 
    ? serviceId 
    : (isTarget ? targetServiceId : selectedServiceId);
  
  // Use provided client setter or get from store based on isTarget
  const setClient = onClientSelect || (isTarget ? setTargetClient : setSelectedClient);
  const setServiceId = isTarget ? setTargetServiceId : setSelectedServiceId;
  
  // Field names based on whether this is for source or target
  const clientField = isTarget ? "targetClient" : "client";
  const clientNameField = isTarget ? "targetClientName" : "clientName";
  const placeholderText = isTarget ? "Select client" : "Select source client";
  
  // Fetch clients for the selected instance
  const { 
    data: clients = [], 
    isLoading: isLoadingClients,
    error: clientsError
  } = useServices(instanceId);
  
  // Handle client selection change
  const handleClientChange = (value: string) => {
    if (
      value === "loading-state" ||
      value === "error-state" ||
      value === "no-clients-state"
    ) {
      return;
    }
    
    // Update the form field and selected client ID
    updateFormField(clientField, value);
    setServiceId(value);
    
    // Find the selected client to get its name and store the full object
    const newSelectedClient = clients.find(client => client.serviceId.toString() === value);
    if (newSelectedClient) {
      updateFormField(clientNameField, newSelectedClient.serviceName);
      setClient(newSelectedClient);
    }
  };
  
  // Reset selected client when instance changes
  useEffect(() => {
    if (instanceId) {
      setServiceId(null);
      setClient(null);
      updateFormField(clientField, "");
      updateFormField(clientNameField, "");
    }
  }, [instanceId, setServiceId, setClient, updateFormField, clientField, clientNameField]);
  
  return (
    <div>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor={clientField}>{label}</Label>
        <Select 
          value={currentServiceId?.toString() || undefined} 
          onValueChange={handleClientChange}
        >
          <SelectTrigger id={clientField} className="w-full">
            <SelectValue placeholder={placeholderText} />
          </SelectTrigger>
          <SelectContent>
            {isLoadingClients ? (
              <SelectItem value="loading-state" disabled>
                Loading clients...
              </SelectItem>
            ) : clientsError ? (
              <SelectItem value="error-state" disabled>
                Error: {clientsError instanceof Error ? clientsError.message : "Failed to load clients"}
              </SelectItem>
            ) : clients.length === 0 ? (
              <SelectItem value="no-clients-state" disabled>
                No clients available
              </SelectItem>
            ) : (
              clients.map((client: Client) => (
                <SelectItem key={client.serviceId} value={client.serviceId.toString()}>
                  {client.serviceName}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default ClientSelector;
